import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { accountRole } from "pages/dashboard/dashboardFeatures";

import {
  Calendar,
  Components,
  Event,
  Formats,
  momentLocalizer,
  SlotInfo,
} from "react-big-calendar";
import moment from "moment";

import "react-big-calendar/lib/css/react-big-calendar.css";
import { Box, GlobalStyles, useMediaQuery, useTheme } from "@mui/material";
import { useMeQuery } from "generated/graphql";
import { useSnack } from "hooks/useSnack";
import Toolbar from "./toolbar";
import LockedOverlay from "./setAvailability/LockedOverlay";
import Legend from "./setAvailability/Legend";
import AvailabilityModal from "./setAvailability/AvailabilityModal";
import { AvailabilityData } from "./setAvailability/types";
import { useVendorAvailability } from "./setAvailability/availabilityApi";
import { BookingType, EVENT_TYPES, EVENT_TYPE_MAP } from "./eventTypes";
import { Booking } from "./booking/types";
import BookingModal from "./booking/components/BookingModal";
import BookAppointmentModal from "./booking/components/BookAppointmentModal";
import { useBookableVendors } from "./booking/components/BookAppointmentModal/bookAppointmentApi";
import EventDetailsPanel from "./booking/components/EventDetailsPanel";
import DeleteBookingDialog from "./booking/components/DeleteBookingDialog";
import BookingBanner from "./booking/components/BookingBanner";
import FilterMenu from "./booking/components/FilterMenu";
import CalendarEvent from "./booking/components/CalendarEvent";
import { useLeads, useMyQuotes } from "./booking/leadSuggestions";
import {
  BookingResponseType,
  ProposedTime,
  useInvitedBookings,
  useVendorBookings,
} from "./booking/bookingsApi";

const ALL_TYPE_IDS = EVENT_TYPES.map((t) => t.id);

const bookingToEvent = (b: Booking): Event => {
  if (b.allDay) {
    return {
      title: b.title,
      start: moment(b.date).startOf("day").toDate(),
      end: moment(b.date).endOf("day").toDate(),
      allDay: true,
      resource: b,
    };
  }
  return {
    title: b.title,
    start: moment(`${b.date} ${b.from}`, "YYYY-MM-DD HH:mm").toDate(),
    end: moment(`${b.date} ${b.to}`, "YYYY-MM-DD HH:mm").toDate(),
    resource: b,
  };
};

const MyCalendar: React.FC = () => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const showSnack = useSnack();
  const { t } = useTranslation("calendar");
  const localizer = useMemo(() => momentLocalizer(moment), []);

  const [{ data }] = useMeQuery();
  const isVendor = accountRole(data?.me) === "vendor";

  const { availability, save } = useVendorAvailability(isVendor);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const isLocked = isVendor && availability === null;

  // --- Bookings (persisted via GraphQL) ---
  const { suggestions: leadSuggestions, byEmail: leadsByEmail } =
    useLeads(isVendor);
  const isCustomer = !!data?.me && !isVendor;
  const currentUserEmail = data?.me?.email ?? undefined;

  const {
    bookings: vendorBookings,
    save: saveBooking,
    remove: removeBooking,
  } = useVendorBookings(isVendor);
  const { bookings: invitedBookings, respond } = useInvitedBookings(isCustomer);
  const myQuotes = useMyQuotes(isCustomer);
  const bookableVendors = useBookableVendors(isCustomer);
  const bookings = isVendor ? vendorBookings : invitedBookings;

  const [bookingSaving, setBookingSaving] = useState(false);
  const [responding, setResponding] = useState(false);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [bookAppointmentOpen, setBookAppointmentOpen] = useState(false);
  const [bookAppointmentDate, setBookAppointmentDate] = useState<
    string | undefined
  >(undefined);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [defaultSlot, setDefaultSlot] = useState<{
    date: string;
    from?: string;
    to?: string;
  } | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [deletingBooking, setDeletingBooking] = useState<Booking | null>(null);
  const [banner, setBanner] = useState<Booking | null>(null);

  // --- Filter ---
  const [filterTypes, setFilterTypes] = useState<BookingType[]>(ALL_TYPE_IDS);
  const [filterOpen, setFilterOpen] = useState(false);
  const filterAnchorRef = useRef<HTMLDivElement>(null);

  const openNewBooking = (slot?: {
    date: string;
    from?: string;
    to?: string;
  }) => {
    setEditingBooking(null);
    setDefaultSlot(slot ?? null);
    setBookingModalOpen(true);
  };

  const handleSaveBooking = async (booking: Booking, newFiles: File[]) => {
    setBookingSaving(true);
    const result = await saveBooking(booking, newFiles);
    setBookingSaving(false);
    if (!result.ok) {
      showSnack(result.error ?? t("snack.save_booking_failed"), "error");
      return;
    }
    const saved = result.booking ?? booking;
    setBookingModalOpen(false);
    setEditingBooking(null);
    setDefaultSlot(null);
    setBanner(saved);
    setSelectedBooking((prev) => (prev && prev.id === saved.id ? saved : prev));
  };

  const handleConfirmDelete = async () => {
    if (!deletingBooking) {
      return;
    }
    const target = deletingBooking;
    const result = await removeBooking(target.id);
    if (!result.ok) {
      showSnack(result.error ?? t("snack.delete_booking_failed"), "error");
      return;
    }
    setSelectedBooking((prev) => (prev && prev.id === target.id ? null : prev));
    if (banner?.id === target.id) {
      setBanner(null);
    }
    setDeletingBooking(null);
    showSnack(t("snack.booking_deleted"), "success");
  };

  const handleRespond = async (
    response: BookingResponseType,
    proposed?: ProposedTime,
  ) => {
    if (!selectedBooking) {
      return;
    }
    setResponding(true);
    const result = await respond(selectedBooking.id, response, proposed);
    setResponding(false);
    if (!result.ok) {
      showSnack(result.error ?? t("snack.respond_failed_short"), "error");
      return;
    }
    if (result.booking) {
      setSelectedBooking(result.booking);
    }
    showSnack(
      response === "accept"
        ? t("snack.accepted")
        : response === "decline"
          ? t("snack.declined")
          : t("snack.proposed"),
      "success",
    );
  };

  // Vendor applies an attendee's proposed time by opening the edit form prefilled.
  const handleApplyProposedTime = (date: string, from: string, to: string) => {
    if (!selectedBooking) {
      return;
    }
    setEditingBooking({ ...selectedBooking, date, from, to, allDay: false });
    setDefaultSlot(null);
    setBookingModalOpen(true);
  };

  const components: Components = useMemo(
    () => ({
      toolbar: (toolbarProps) => (
        <Toolbar
          {...toolbarProps}
          isVendor={isVendor}
          isLocked={isLocked}
          onSetAvailability={() => setAvailabilityOpen(true)}
          onNewBooking={() => openNewBooking()}
          onBookAppointment={() => {
            setBookAppointmentDate(undefined);
            setBookAppointmentOpen(true);
          }}
          onFilter={() => setFilterOpen(true)}
          filterRef={filterAnchorRef}
        />
      ),
      event: CalendarEvent,
    }),
    [isVendor, isLocked],
  );

  const formats: Formats | undefined = useMemo(() => {
    return {
      dateFormat: "D",
      // A month column is ~55px on a phone, which cannot hold "Wednesday".
      weekdayFormat: (date, culture) =>
        localizer.format(date, isCompact ? "ddd" : "dddd", culture),
    };
  }, [localizer, isCompact]);

  const eventPropGetter = (event: Event) => {
    const booking = event.resource as Booking | undefined;
    const color = booking
      ? EVENT_TYPE_MAP[booking.type].color
      : theme.palette.warning.main;
    return {
      style: {
        backgroundColor: `${color}26`,
        borderLeft: `3px solid ${color}`,
        borderRadius: 2,
        color: theme.palette.text.primary,
        padding: "1px 4px",
        fontSize: "0.8rem",
      },
    };
  };

  const events: Event[] = useMemo(
    () =>
      bookings.filter((b) => filterTypes.includes(b.type)).map(bookingToEvent),
    [bookings, filterTypes],
  );

  const handleSelectSlot = (slot: SlotInfo) => {
    const start = moment(slot.start);

    // Customer: clicking a day opens the appointment request prefilled with it.
    if (isCustomer) {
      setBookAppointmentDate(start.format("YYYY-MM-DD"));
      setBookAppointmentOpen(true);
      return;
    }

    if (!isVendor || isLocked) {
      return;
    }
    const end = moment(slot.end);
    const durationHours = end.diff(start, "hours", true);
    const timed = durationHours > 0 && durationHours < 24;
    openNewBooking({
      date: start.format("YYYY-MM-DD"),
      from: timed ? start.format("HH:mm") : undefined,
      to: timed ? end.format("HH:mm") : undefined,
    });
  };

  const handleSaveAvailability = async (saved: AvailabilityData) => {
    const result = await save(saved);
    if (result.ok) {
      setAvailabilityOpen(false);
      showSnack(t("snack.availability_saved"), "success");
    } else {
      showSnack(result.error ?? t("snack.save_availability_failed"), "error");
    }
  };

  return (
    <div
      style={{
        height: "95%",
        paddingTop: theme.spacing(1),
        display: "flex",
        flexDirection: "column",
      }}
    >
      <GlobalStyles
        styles={{
          ".rbc-month-view .rbc-header": {
            padding: isCompact
              ? theme.spacing(0.75, 0.25)
              : `${theme.spacing(1.5)} ${theme.spacing(1)}`,
            background: theme.palette.common.black,
            color: theme.palette.common.white,
            fontWeight: 600,
            fontSize: isCompact ? "0.7rem" : undefined,
            borderBottom: "1px solid black",
          },
          ".rbc-month-view .rbc-date-cell": {
            display: "flex",
            justifyContent: "flex-start",
            padding: isCompact ? theme.spacing(0.25) : theme.spacing(1),
            fontSize: isCompact ? "0.75rem" : undefined,
          },
          // Event pills inherit the column width, so they need to give up their
          // gutters too or the title is clipped to a couple of characters.
          ...(isCompact
            ? { ".rbc-event": { padding: "1px 2px", fontSize: "0.7rem" } }
            : {}),
          ".rbc-month-view": {
            border: "1px solid black",
          },
          ".rbc-month-view .rbc-day-bg + .rbc-day-bg": {
            borderLeft: "1px solid black",
          },
        }}
      />

      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            position: "relative",
            flex: 1,
            minHeight: 0,
            // Without this the grid refuses to shrink below its content and
            // pushes the details panel off the side instead of narrowing.
            minWidth: 0,
          }}
        >
          {isLocked && (
            <LockedOverlay
              onSetAvailability={() => setAvailabilityOpen(true)}
            />
          )}

          {banner && (
            <div
              style={{
                position: "absolute",
                top: theme.spacing(1),
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 4,
                maxWidth: "90%",
              }}
            >
              <BookingBanner
                booking={banner}
                onView={() => {
                  setSelectedBooking(banner);
                  setBanner(null);
                }}
                onDismiss={() => setBanner(null)}
              />
            </div>
          )}

          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            components={components}
            formats={formats}
            drilldownView={null}
            culture={moment.locale()}
            messages={{
              showMore: (count: number) => t("show_more", { count }),
            }}
            eventPropGetter={eventPropGetter}
            selectable={(isVendor && !isLocked) || isCustomer}
            onSelectSlot={handleSelectSlot}
            onSelectEvent={(event) =>
              setSelectedBooking((event as Event).resource as Booking)
            }
          />
        </Box>

        {selectedBooking && (
          <EventDetailsPanel
            booking={selectedBooking}
            viewerRole={isVendor ? "vendor" : "customer"}
            currentUserEmail={currentUserEmail}
            leadsByEmail={leadsByEmail}
            customerQuotes={myQuotes}
            responding={responding}
            onClose={() => setSelectedBooking(null)}
            onEdit={() => {
              setEditingBooking(selectedBooking);
              setDefaultSlot(null);
              setBookingModalOpen(true);
            }}
            onDelete={() => setDeletingBooking(selectedBooking)}
            onApplyProposedTime={handleApplyProposedTime}
            onRespond={handleRespond}
          />
        )}
      </Box>

      <Legend />

      {availabilityOpen && (
        <AvailabilityModal
          initial={availability ?? null}
          onClose={() => setAvailabilityOpen(false)}
          onSave={handleSaveAvailability}
        />
      )}

      {bookingModalOpen && (
        <BookingModal
          existing={editingBooking}
          defaultDate={defaultSlot?.date}
          defaultFrom={defaultSlot?.from}
          defaultTo={defaultSlot?.to}
          leadSuggestions={leadSuggestions}
          leadsByEmail={leadsByEmail}
          saving={bookingSaving}
          onClose={() => {
            setBookingModalOpen(false);
            setEditingBooking(null);
            setDefaultSlot(null);
          }}
          onSave={handleSaveBooking}
        />
      )}

      {bookAppointmentOpen && (
        <BookAppointmentModal
          vendors={bookableVendors}
          defaultDate={bookAppointmentDate}
          onClose={() => setBookAppointmentOpen(false)}
        />
      )}

      {deletingBooking && (
        <DeleteBookingDialog
          booking={deletingBooking}
          onCancel={() => setDeletingBooking(null)}
          onConfirm={handleConfirmDelete}
        />
      )}

      <FilterMenu
        anchorEl={filterAnchorRef.current}
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        selected={filterTypes}
        onChange={setFilterTypes}
      />
    </div>
  );
};

export default MyCalendar;
